import { createContext, useContext, useMemo } from "react";
import { isSameDay, parseISO, startOfYear, endOfYear, subYears, addYears, startOfToday, format, isAfter, isBefore, addMonths, subMonths, isSameMonth } from "date-fns";

export const CalendarContext = createContext();
CalendarContext.displayName = 'Calendar';

export function useCalendarData() {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error('useCalendarData must be used within a <Calendar />');
  }
  return context;
}

export function isSameMatchDay(items, matchDay) {
  if (!items) {
    return null;
  }

  return items.find(item => {
    const date = parseISO(item.date);

    return isSameDay(date, matchDay);
  });
}

export function getToday() {
  return useMemo(() => startOfToday(), []);
}

export function getStartAndEndOfYear(currentYear) {
  if (!currentYear || !(currentYear instanceof Date)) {
    return { start: null, end: null };
  }

  return {
    start: startOfYear(currentYear),
    end: endOfYear(currentYear)
  };
}

export function getYearsData(currentMonth, { minDate, maxDate }) {
  return useMemo(() => {
    const previousYears = [];
    const nextYears = [];

    let disablePreviousYears = false;
    let disableNextYears = false;

    for (let i = 6; i > 0; i--) {
      const temp = subYears(currentMonth, i);
      if (minDate && maxDate) {
        if (isAfter(temp, minDate) && isBefore(temp, maxDate)) {
          previousYears.push(temp);
        } else {
          disablePreviousYears = true;
        }
      } else {
        previousYears.push(temp);
      }
    }

    for (let i = 1; i < 6; i++) {
      const temp = addYears(currentMonth, i);
      if (maxDate && minDate) {
        if (isBefore(temp, maxDate) && isAfter(temp, minDate)) {
          nextYears.push(temp);
        } else {
          disableNextYears = true;
        }
      } else {
        nextYears.push(temp);
      }
    }

    const yearsRange = [...previousYears, currentMonth, ...nextYears];
    const formatFirstYear = format(yearsRange[0], 'y');
    const formatLastYear = format(yearsRange[yearsRange.length - 1], 'y');

    return {
      disablePreviousYears,
      disableNextYears,
      yearsRange,
      yearsLabel: `${formatFirstYear} - ${formatLastYear}`
    };
  }, [currentMonth]);
}

export const calendarViews = {
  months: 'months',
  days: 'days',
  years: 'years'
};

export function getIsNextMonthDisabled({ date, minDate, maxDate }) {

  if (minDate && maxDate) {
    const nextMonth = addMonths(date, 1);
    return isAfter(nextMonth, maxDate) || isSameDay(nextMonth, maxDate)
  }

  return false;
}

export function getIsPreviousMonthDisabled({ date, minDate, maxDate }) {
  if (minDate && maxDate) {
    const previousMonth = subMonths(date, 1);
    return isBefore(previousMonth, minDate) || isSameDay(previousMonth, minDate)
  }

  return false;
}

export function getIsNextMonthsDisabled({ activeMonths = [], minDate, maxDate }) {
  if (minDate && maxDate) {
    return !!activeMonths.find(({ date }) => getIsNextMonthDisabled({ date, minDate, maxDate }))
  }

  return false;
}

export function getIsPreviousMonthsDisabled({ activeMonths = [], minDate, maxDate }) {
  if (minDate && maxDate) {
    return !!activeMonths.find(({ date }) => getIsPreviousMonthDisabled({ date, minDate, maxDate }))
  }

  return false;
}